import { IonModal } from "@ionic/react";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
  onSubmit: (reportDetails: { email: string; description: string }) => void;
}

const ReportBugModal: FC<Props> = ({ isOpen, onDismiss, onSubmit }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!email.trim() || !description.trim()) {
      setError(t("common_all_fields_required"));
      return;
    }
    setError(null);
    onSubmit({ email, description });
    onDismiss();
  };

  return (
    <IonModal
      cssClass="resonance-modal"
      isOpen={isOpen}
      backdropDismiss={true}
      onWillDismiss={onDismiss}
    >
      <Container onClick={onDismiss}>
        <Card onClick={(e) => e.stopPropagation()}>
          <Title>{t("profile_report_bug")}</Title>
          <Form>
            <Label>{t("common_your_email")}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Label>{t("common_bug_description")}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation(); // Prevent parent/global listeners
                }
              }}
            />
            {error && <Error>{error}</Error>}
          </Form>
          <Buttons>
            <ModalButton onClick={onDismiss}>{t("common_cancel")}</ModalButton>
            <ModalButton onClick={handleSubmit}>{t("common_submit")}</ModalButton>
          </Buttons>
        </Card>
      </Container>
    </IonModal>
  );
};

const Container = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 400px;
  background-color: white;
  border-radius: 10px;
  padding: 20px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
`;

const Form = styled.div`
  margin-top: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  margin-bottom: 5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
  background-color: white; /* Explicit background color */
  color: black; /* Explicit text color */
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.primary.main};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 150px; /* Adjust height for better visibility */
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
  resize: vertical; /* Allow resizing */
  overflow-y: auto; /* Enable scrolling */
  background-color: white; /* Explicit background color */
  color: black; /* Explicit text color */
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.primary.main};
  }
`;

const Error = styled.div`
  color: red;
  font-size: 12px;
  margin-top: 5px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const ModalButton = styled.div`
  padding: 10px 20px;
  color: ${(props) => props.theme.primary.main};
  font-weight: bold;
  cursor: pointer;
  border: 1px solid ${(props) => props.theme.primary.main};
  border-radius: 5px;
  margin-left: 10px;
`;

export default ReportBugModal;
